/**
 * The navigation preference's storage adapter: Firestore, plus a local cache.
 *
 * Two-phase, the same shape as `theme-store.js` and `appearance-store.js`,
 * and for the same reason those are two-phase. Reading the settings document
 * is a network round trip. An advisor who chose the sidebar and waits for
 * Firestore before it paints watches the top bar appear and then be replaced
 * — the layout jumping under their cursor on every single load.
 *
 * So: paint from the local copy the instant the uid is known, then reconcile.
 * In practice the second phase changes nothing, which is the point.
 *
 * ── Why the local copy is `/app`-shaped ──────────────────────────────────
 *
 * `lib/appearance.js#legacyKey` writes `u:<uid>:<key>` as a bare string —
 * the format `/app`'s own `MBMStore` uses. Reused here even though `/app` has
 * no sidebar and never will: one key format for every synced setting is worth
 * more than a second one that is marginally tidier, and if the legacy program
 * ever reads this key it will read something it understands rather than JSON
 * with quotes in it (the exact bug `lib/theme.js#legacyThemeKey` records).
 */

import { readDoc, mergeDoc } from './firebase.js'
import { wrap } from './settings.js'
import { readLegacy, writeLegacy } from './appearance.js'
import {
  NAV_KEY,
  RAIL_KEY,
  applyNav,
  normalizeNav,
  normalizeRail,
  resolveNav,
} from './nav-prefs.js'

/** What is in the browser right now, before any network call. */
function cached(uid) {
  return resolveNav({
    localNav: readLegacy(uid, NAV_KEY),
    localRail: readLegacy(uid, RAIL_KEY),
  })
}

/**
 * Phase one: paint the navigation we can know without waiting.
 *
 * @param {string} uid
 * @returns {{nav: 'topbar'|'sidebar', rail: 'collapsed'|'expanded'}}
 */
export function applyCachedNav(uid) {
  const found = cached(uid)
  return applyNav(found.nav, found.rail)
}

/**
 * Phase two: reconcile against the shared settings document.
 *
 * A failed read leaves the cached choice in place rather than snapping back
 * to the top bar. An advisor's menu rearranging itself because a network call
 * failed is worse than being one round trip out of date.
 *
 * @param {string} uid
 * @returns {Promise<{nav: 'topbar'|'sidebar', rail: 'collapsed'|'expanded'}>}
 */
export async function syncNavFromCloud(uid) {
  const before = cached(uid)
  try {
    const settings = await readDoc(['users', uid, 'settings', 'main'])
    const found = resolveNav({
      settings,
      localNav: readLegacy(uid, NAV_KEY),
      localRail: readLegacy(uid, RAIL_KEY),
    })
    if (found.nav !== before.nav || found.rail !== before.rail) {
      applyNav(found.nav, found.rail)
      writeLegacy(uid, NAV_KEY, found.nav)
      writeLegacy(uid, RAIL_KEY, found.rail)
    }
    return found
  } catch (err) {
    console.error('navigation sync failed, keeping the cached choice', err)
    return before
  }
}

/**
 * Write one of the two, and make it stick.
 *
 * Order matters, and it is the order `setTheme` uses: paint first, persist
 * after. The advisor asked for a visual change; making them wait on a network
 * write to see it is the wrong trade, and a failed write must not mean a dead
 * control.
 *
 * @param {string} uid
 * @param {{nav?: unknown, rail?: unknown}} patch
 * @returns {Promise<{nav: 'topbar'|'sidebar', rail: 'collapsed'|'expanded'}>}
 */
export async function setNavPref(uid, patch) {
  const now = cached(uid)
  const next = {
    nav: patch.nav === undefined ? now.nav : normalizeNav(patch.nav),
    rail: patch.rail === undefined ? now.rail : normalizeRail(patch.rail),
  }

  applyNav(next.nav, next.rail)
  writeLegacy(uid, NAV_KEY, next.nav)
  writeLegacy(uid, RAIL_KEY, next.rail)

  /* Only what actually changed goes to the document. Writing both every time
     would have the rail's collapse arrow stamp a `mbm_nav` timestamp too, and
     `/app`'s last-write-wins comparison reads those timestamps. */
  const patchDoc = /** @type {Record<string, any>} */ ({})
  const at = new Date().toISOString()
  if (patch.nav !== undefined) patchDoc[NAV_KEY] = wrap(next.nav, at)
  if (patch.rail !== undefined) patchDoc[RAIL_KEY] = wrap(next.rail, at)

  if (Object.keys(patchDoc).length) {
    try {
      await mergeDoc(['users', uid, 'settings', 'main'], patchDoc)
    } catch (err) {
      console.error('navigation save failed; applied locally but will not follow you', err)
    }
  }
  return next
}
